import {
  ALL_REALS,
  type IntervalSet,
  intersectSets,
  NO_REALS,
  type Relation,
  setContains,
  unionSets,
} from "@/lib/tool-kit/algebra/intervals";
import { normalizeAlgebra } from "@/lib/tool-kit/algebra/parse";
import type { Poly } from "@/lib/tool-kit/algebra/poly";
import {
  type RelationSolution,
  solveRelation,
} from "@/lib/tool-kit/algebra/relation";
import {
  parseSides,
  type StatementError,
  singleVariable,
} from "@/lib/tool-kit/algebra/statement";
import { type Uni, uniEval } from "@/lib/tool-kit/algebra/uni";
import {
  addR,
  compareR,
  divR,
  intR,
  type Rational,
} from "@/lib/tool-kit/math/rational";

/**
 * Linear inequalities, single, double (`−3 < 2x + 1 ≤ 7`) and joined by "and" / "or".
 *
 * Each clause is solved by the step engine in `relation.ts`; the clause solution sets are
 * then intersected ("and") or united ("or"), with "and" binding tighter than "or".
 */
export interface Clause {
  /** The clause as typed; null for the halves of a chain that had to be split. */
  source: string | null;
  parts: Uni[];
  rels: Relation[];
  solution: RelationSolution;
}

export type Connector = "and" | "or";

export type InequalityError =
  | StatementError
  | { code: "no-inequality"; token: string }
  | { code: "too-many-relations"; token: string }
  | { code: "too-many-clauses"; token: string }
  | { code: "nonlinear"; token: string };

export type InequalityReading =
  | { ok: false; error: InequalityError }
  | {
      ok: true;
      variable: string;
      clauses: Clause[];
      joins: Connector[];
      set: IntervalSet;
    };

const MAX_CLAUSES = 4;
const CLAUSE_SPLIT = /(\s+(?:and|or)\s+|[∧∨])/i;
const RELATION_SPLIT = /(<=|>=|≤|≥|<|>)/;
const EQUALS = /=/;

function relationOf(token: string): Relation {
  switch (token) {
    case "<=":
    case "≤":
      return "≤";
    case ">=":
    case "≥":
      return "≥";
    default:
      return token as Relation;
  }
}

function connectorOf(token: string): Connector {
  const text = token.trim().toLowerCase();
  return text === "or" || text === "∨" ? "or" : "and";
}

interface RawClause {
  sides: string[];
  rels: Relation[];
}

function splitClause(text: string): RawClause | InequalityError {
  const pieces = text.split(RELATION_SPLIT);
  const sides: string[] = [];
  const rels: Relation[] = [];
  pieces.forEach((piece, index) => {
    if (index % 2 === 0) {
      sides.push(piece);
    } else {
      rels.push(relationOf(piece));
    }
  });
  if (rels.length === 0) {
    return {
      code: EQUALS.test(text) ? "no-inequality" : "no-relation",
      token: text.trim(),
    };
  }
  if (rels.length > 2) {
    return { code: "too-many-relations", token: text.trim() };
  }
  return { sides, rels };
}

function isRawClause(value: RawClause | InequalityError): value is RawClause {
  return "sides" in value;
}

interface Built {
  clauses: Clause[];
  joins: Connector[];
}

/** One user clause as one or two engine clauses (a chain with the variable outside splits). */
function expandChain(
  parts: Uni[],
  rels: Relation[]
): { parts: Uni[]; rels: Relation[] }[] {
  const outsideVariable =
    parts.length === 3 && (parts[0].length > 1 || parts[2].length > 1);
  if (outsideVariable) {
    return [
      { parts: [parts[0], parts[1]], rels: [rels[0]] },
      { parts: [parts[1], parts[2]], rels: [rels[1]] },
    ];
  }
  return [{ parts, rels }];
}

export function solveInequalityText(input: string): InequalityReading {
  const text = normalizeAlgebra(input);
  const tokens = text.split(CLAUSE_SPLIT);
  const texts: string[] = [];
  const connectors: Connector[] = [];
  tokens.forEach((token, index) => {
    if (index % 2 === 0) {
      texts.push(token);
    } else {
      connectors.push(connectorOf(token));
    }
  });
  if (texts.length > MAX_CLAUSES) {
    return {
      ok: false,
      error: { code: "too-many-clauses", token: String(MAX_CLAUSES) },
    };
  }
  const raws: RawClause[] = [];
  for (const clause of texts) {
    const raw = splitClause(clause);
    if (!isRawClause(raw)) {
      return { ok: false, error: raw };
    }
    raws.push(raw);
  }
  const polys: Poly[][] = [];
  for (const raw of raws) {
    const parsed = parseSides(raw.sides);
    if (!parsed.ok) {
      return { ok: false, error: parsed.error };
    }
    polys.push(parsed.polys);
  }
  const read = singleVariable(polys.flat());
  if (!read.ok) {
    return { ok: false, error: read.error };
  }
  const built = build(
    polys,
    raws,
    connectors,
    read.variable,
    texts.map((x) => x.trim())
  );
  if ("code" in built) {
    return { ok: false, error: built };
  }
  return {
    ok: true,
    variable: read.variable,
    clauses: built.clauses,
    joins: built.joins,
    set: combine(built.clauses, built.joins),
  };
}

function build(
  polys: Poly[][],
  raws: RawClause[],
  connectors: Connector[],
  variable: string,
  sources: string[]
): Built | InequalityError {
  const clauses: Clause[] = [];
  const joins: Connector[] = [];
  for (const [index, group] of polys.entries()) {
    const read = singleVariable(group.map((p) => p));
    if (!read.ok) {
      return read.error;
    }
    const parts = read.parts;
    const pieces = expandChain(parts, raws[index].rels);
    pieces.forEach((piece, k) => {
      clauses.push({
        ...piece,
        source: pieces.length === 1 ? sources[index] : null,
        solution: solveRelation(piece.parts, piece.rels),
      });
      if (k > 0) {
        joins.push("and");
      }
    });
    if (index < connectors.length) {
      joins.push(connectors[index]);
    }
  }
  const nonlinear = clauses.find(
    (c) => c.solution.outcome.kind === "nonlinear"
  );
  if (nonlinear) {
    return { code: "nonlinear", token: variable };
  }
  return { clauses, joins };
}

function clauseSet(clause: Clause): IntervalSet {
  switch (clause.solution.outcome.kind) {
    case "solved":
      return clause.solution.set;
    case "identity":
      return ALL_REALS;
    default:
      return NO_REALS;
  }
}

/** "and" binds tighter than "or": split at the "or"s, intersect inside, unite across. */
export function combine(clauses: Clause[], joins: Connector[]): IntervalSet {
  let result: IntervalSet = NO_REALS;
  let group = clauseSet(clauses[0]);
  joins.forEach((join, index) => {
    const next = clauseSet(clauses[index + 1]);
    if (join === "and") {
      group = intersectSets(group, next);
    } else {
      result = unionSets(result, group);
      group = next;
    }
  });
  return unionSets(result, group);
}

/** A number inside the set, for a sample check; null when the set is empty. */
export function samplePoint(set: IntervalSet): Rational | null {
  const first = set[0];
  if (!first) {
    return null;
  }
  const two = intR(2);
  let value: Rational;
  if (first.lo && first.hi) {
    value = divR(addR(first.lo.value, first.hi.value), two);
  } else if (first.lo) {
    value = addR(first.lo.value, intR(1));
  } else if (first.hi) {
    value = addR(first.hi.value, intR(-1));
  } else {
    value = intR(0);
  }
  return setContains(set, value) ? value : null;
}

export interface SampleCheck {
  values: Rational[];
  holds: boolean;
}

/** The clause's original parts evaluated at `x`, and whether every relation holds. */
export function checkClause(clause: Clause, x: Rational): SampleCheck {
  const values = clause.parts.map((p) => uniEval(p, x));
  const holds = clause.rels.every((rel, i) => {
    const order = compareR(values[i], values[i + 1]);
    switch (rel) {
      case "<":
        return order < 0;
      case "≤":
        return order <= 0;
      case ">":
        return order > 0;
      case "≥":
        return order >= 0;
      default:
        return order === 0;
    }
  });
  return { values, holds };
}
